<template>
  <SrpModal v-if="props.isVisible" :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', $event)" className="location-posts-modal" :width="960" maxHeight="100%">
    <template #header>
      <div class="location-posts__header">
        <LocationAvatar v-if="location" :src="location.icon" :name="location.name" :size="56" />
        <div class="location-posts__identity">
          <h2 class="global-h2 location-posts__title">{{ location?.name ?? "Location posts" }}</h2>
          <LocationSocialHandles v-if="hasSocialHandles" :location="location" />
          <div v-else-if="location?.city" class="location-posts__city">{{ location.city }}</div>
        </div>
      </div>
      <div v-if="response" class="location-posts__summary">
        <div class="location-posts__summary-total">
          <IconEmbedded name="megaphone_2" :size="28" />
          <strong>{{ highlightSummary.headline }}</strong>
        </div>
        <span class="location-posts__summary-recency">{{ highlightSummary.recency }}</span>
      </div>
    </template>
    <template #content>
      <div v-if="isLoading" class="location-posts__message" role="status">
        <LoadingIcon :size="28" />
        <span>Loading posts…</span>
      </div>
      <div v-else-if="loadError" class="location-posts__message" role="alert">
        <p>{{ loadError }}</p>
        <SrpButton tag="button" color="gray" fill="outlined" size="small" @click="loadPosts">Try again</SrpButton>
      </div>
      <template v-else-if="response">
        <div v-if="!filteredPosts.length" class="location-posts__empty">
          {{ response.posts.length ? "No posts match the selected filters." : "This location hasn't been highlighted in this calendar yet." }}
        </div>
        <div v-else class="location-posts__grid">
          <article v-for="post in visiblePosts" :key="post.id" class="location-posts__post">
            <SocialPostPreview :post="post" readonlyMode isStatusAndSocialsPanelVisible isPostScheduleVisible :isDateGroupedByWeek="false" />
          </article>
        </div>
        <div v-if="visiblePosts.length < filteredPosts.length" class="location-posts__more">
          <SrpButton tag="button" color="gray" fill="outlined" size="small" @click="visibleCount += PAGE_SIZE">Show more</SrpButton>
          <span>Showing {{ visiblePosts.length }} of {{ filteredPosts.length }} posts</span>
        </div>
      </template>
    </template>
    <template #footer="{ closeModal }">
      <div class="location-posts__footer">
        <span class="location-posts__filter-note">{{ filterDescription }}</span>
        <SrpButton tag="button" color="gray" fill="outlined" @click="closeModal()">Close</SrpButton>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationSocialHandles from "./LocationSocialHandles.vue";
import SocialReportsRepo from "@repos/SocialReportsRepo";
import { buildLocationHighlightSummary, filterLocationPosts } from "@logic/LocationPosts";
import type { LocationHighlightsPostFilter, LocationPostsResponse } from "@contracts/socialReports";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    calendarId: string;
    locationId: string;
    postFilter?: LocationHighlightsPostFilter;
    startDate?: string | null;
    endDate?: string | null;
    /** Optional friendly description, e.g. "Only Published · Last Quarter". */
    filterLabel?: string;
  }>(),
  { postFilter: "All", startDate: null, endDate: null, filterLabel: "" }
);

const emit = defineEmits<{ (e: "update:isVisible", value: boolean): void }>();
const PAGE_SIZE = 12;
const response = ref<LocationPostsResponse | null>(null);
const isLoading = ref(false);
const loadError = ref("");
const visibleCount = ref(PAGE_SIZE);
let loadSequence = 0;
let opener: HTMLElement | null = null;
const location = computed(() => response.value?.location);
const hasSocialHandles = computed(() => Boolean(location.value?.socialSet?.instagramHandle || location.value?.socialSet?.facebookPageName || location.value?.socialSet?.tikTokHandle));
const filteredPosts = computed(() => filterLocationPosts(response.value?.posts ?? [], props.postFilter, props.startDate, props.endDate));
const visiblePosts = computed(() => filteredPosts.value.slice(0, visibleCount.value));
const highlightSummary = computed(() => buildLocationHighlightSummary(filteredPosts.value));
const filterDescription = computed(() => {
  if (props.filterLabel) return props.filterLabel;
  const status = { All: "All Posts (including drafts)", OnlyApproved: "Only Approved", OnlyPublished: "Only Published" }[props.postFilter];
  const dates = props.startDate || props.endDate ? `${props.startDate ?? "Beginning"} – ${props.endDate ?? "Present"}` : "Ever";
  return `${status}${dates ? ` · ${dates}` : ""}`;
});

async function loadPosts() {
  const sequence = ++loadSequence;
  response.value = null;
  loadError.value = "";
  visibleCount.value = PAGE_SIZE;
  if (!props.isVisible || !props.customerId || !props.calendarId || !props.locationId) return;
  isLoading.value = true;
  try {
    const result = await SocialReportsRepo.getLocationPosts(props.customerId, props.calendarId, props.locationId);
    if (sequence === loadSequence) response.value = result;
  } catch {
    if (sequence === loadSequence) loadError.value = "We couldn't load these posts. Please try again.";
  } finally {
    if (sequence === loadSequence) isLoading.value = false;
  }
}

watch(
  () => props.isVisible,
  visible => {
    if (visible) opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    else opener?.focus();
  },
  { immediate: true }
);
watch(() => [props.isVisible, props.customerId, props.calendarId, props.locationId], loadPosts, { immediate: true });
watch(
  () => [props.postFilter, props.startDate, props.endDate],
  () => {
    visibleCount.value = PAGE_SIZE;
  }
);
onBeforeUnmount(() => {
  loadSequence++;
});
</script>

<style scoped lang="scss">
.location-posts {
  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  &__identity {
    min-width: 0;
  }
  &__title {
    margin: 0 0 5px;
    overflow-wrap: anywhere;
  }
  &__city {
    color: #667085;
    font-size: 14px;
  }
  &__summary {
    margin-top: 20px;
    padding-bottom: 18px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 24px;
    border-bottom: 1px solid #e7e9ed;
  }
  &__summary-total {
    display: flex;
    align-items: center;
    gap: 14px;
    color: #344054;
    font-size: 20px;
    line-height: 28px;
    strong {
      font-weight: 600;
    }
    > .embedded-icon {
      flex-shrink: 0;
    }
  }
  &__summary-recency {
    padding-left: 24px;
    border-left: 1px solid #d0d5dd;
    color: #667085;
    font-size: 16px;
    line-height: 24px;
  }
  &__grid {
    // The standard preview's network controls extend above the card.
    padding-top: 16px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 48px 24px;
  }
  &__post {
    min-width: 0;
  }
  &__message {
    min-height: 160px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    text-align: center;
    p {
      margin: 0;
    }
  }
  &__empty {
    padding: 32px 16px;
    color: #667085;
    text-align: center;
    background: #f8fafb;
    border-radius: 10px;
  }
  &__more {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
    color: #667085;
    font-size: 13px;
  }
  &__footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  &__filter-note {
    color: #667085;
    font-size: 13px;
    line-height: 1.4;
  }
}
:global(.location-posts-modal .srp-modal__window-in-wrap) {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 40px);
}
:global(.location-posts-modal .srp-modal__header),
:global(.location-posts-modal .srp-modal__footer) {
  flex-shrink: 0;
}
:global(.location-posts-modal .srp-modal__content-wrap) {
  display: flex;
  min-height: 0;
}
@media (max-width: 600px) {
  .location-posts {
    &__header {
      align-items: flex-start;
      gap: 12px;
    }
    &__summary {
      gap: 8px;
    }
    &__summary-total {
      width: 100%;
      font-size: 17px;
      line-height: 24px;
    }
    &__summary-recency {
      padding-left: 44px;
      border-left: 0;
      font-size: 14px;
    }
    &__grid {
      grid-template-columns: minmax(0, 1fr);
    }
    &__footer {
      flex-wrap: wrap;
    }
    &__filter-note {
      flex: 1 1 180px;
    }
  }
}
</style>
